/**
 * Document Viewer Tabs Component
 * Tab bar with edit mode indicators, plan tab support, page panels, and drag-drop reordering
 * 
 * Includes pinned user avatar and project tab for workspace indicator
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { PlanDocument, PlanStatus } from '../../types/plan-document';
import type { TabType, PagePanelId } from '../../types/panel-types';
import type { ActiveProject } from '../../contexts/AIContext';
import type { LockColorCode } from '../../types';
import { authService } from '../../services/auth-service';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '@/components/ui';
import { useArtifactColors, type ArtifactColorName } from '../../hooks/useArtifactColors';

// Map legacy lock color codes to artifact color names
const LOCK_COLOR_TO_ARTIFACT: Record<LockColorCode, ArtifactColorName> = {
  'blue': 'blue',
  'green': 'grass',
  'purple': 'violet',
  'orange': 'orange',
  'pink': 'pink',
  'cyan': 'cyan',
};

// ============================================================================
// Projects Tab - Simple indicator for current project (use Cmd+P to switch)
// ============================================================================
function ProjectsTab({
  activeProject,
  isSelected,
  onSelect,
}: {
  activeProject: ActiveProject | null;
  isSelected: boolean;
  onSelect: () => void;
}) {
  // Determine tab styling based on selection state and active project
  const getTabStyles = () => {
    if (isSelected) {
      // Selected state - use primary color for both states
      return 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90';
    } else {
      // Not selected state
      if (activeProject) {
        // Has active project - show subtle indicator
        return 'bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30';
      } else {
        return 'bg-card hover:bg-accent text-foreground transition-colors';
      }
    }
  };

  const getTextStyles = () => {
    if (isSelected) {
      return 'text-primary-foreground';
    } else if (activeProject) {
      return 'text-primary';
    } else {
      return 'text-foreground';
    }
  };

  return (
    <div
      role="tab"
      aria-selected={isSelected}
      aria-label={activeProject ? `Projects: ${activeProject.projectCode}. Press Command+P to switch` : 'Projects. Press Command+P to switch'}
      onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer flex-shrink-0 transition-all mr-2 ${getTabStyles()}`}
      title={activeProject ? `Current project: ${activeProject.projectCode}\n⌘P to switch projects` : 'Click to view projects\n⌘P to switch projects'}
    >
      {/* Project Icon */}
      <span className="text-xs">
        {activeProject ? '📁' : '📂'}
      </span>
      
      {/* Project Code or "Projects" label */}
      <span className={`text-xs font-medium truncate max-w-[100px] ${getTextStyles()}`}>
        {activeProject ? activeProject.projectCode : 'Projects'}
      </span>
      
      {/* Keyboard hint */}
      <kbd className={`hidden sm:inline-flex text-[9px] px-1 py-0.5 rounded ${
        isSelected 
          ? 'bg-white/20 text-primary-foreground/70' 
          : 'bg-muted text-muted-foreground'
      }`}>
        ⌘P
      </kbd>
    </div>
  );
}

// ============================================================================
// User Avatar Tab with Dropdown - Pinned tab for user settings
// ============================================================================
function UserAvatarTab({
  userEmail,
  onOpenSettings,
  onLogout,
}: {
  userEmail: string;
  onOpenSettings: (panelId: PagePanelId) => void;
  onLogout?: () => void;
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();

  // Check if user is an Administrator
  useEffect(() => {
    authService.getCurrentUser().then(user => {
      if (user?.groups?.includes('Administrators')) {
        setIsAdmin(true);
      }
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isInsideButton = buttonRef.current?.contains(target);
      if (!isInsideDropdown && !isInsideButton) {
        setShowDropdown(false);
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position for portal
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    
    setShowDropdown(!showDropdown);
  };

  const handleOpenPanel = (panelId: PagePanelId) => {
    onOpenSettings(panelId);
    setShowDropdown(false);
  };

  const initials = userEmail.substring(0, 2).toUpperCase();

  return (
    <div ref={buttonRef} className="flex-shrink-0">
      {/* User Avatar Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDropdownClick}
        className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors"
        title={userEmail}
        aria-label={`User menu for ${userEmail}`}
        aria-expanded={showDropdown}
      >
        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">
          {initials}
        </div>
      </Button>

      {/* Dropdown Menu (rendered via portal) */}
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-card rounded-lg shadow-xl border border-border py-1 w-64 z-[9999]"
          // eslint-disable-next-line no-restricted-syntax -- Dynamic positioning for portal
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {userEmail.split('@')[0]}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {userEmail}
                </p>
              </div>
            </div>
          </div>

          {/* Settings Links */}
          <div className="py-1">
            <Button
              variant="ghost"
              onClick={() => handleOpenPanel('my-profile')}
              className="w-full justify-start px-4 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2 rounded-none h-auto"
            >
              <span>👤</span>
              <span>My Profile</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleOpenPanel('preferences')}
              className="w-full justify-start px-4 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2 rounded-none h-auto"
            >
              <span>⚙️</span>
              <span>Preferences</span>
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={() => handleOpenPanel('users')}
                className="w-full justify-start px-4 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2 rounded-none h-auto"
              >
                <span>👥</span>
                <span>User Management</span>
              </Button>
            )}
          </div>

          {/* Quick Theme Toggle */}
          <div className="px-4 py-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">Theme</p>
            <div className="flex gap-1">
              <Button
                variant={theme === 'light' ? 'default' : 'secondary'}
                size="xs"
                onClick={() => setTheme('light')}
                className="flex-1"
              >
                ☀️
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'secondary'}
                size="xs"
                onClick={() => setTheme('dark')}
                className="flex-1"
              >
                🌙
              </Button>
              <Button
                variant={theme === 'auto' ? 'default' : 'secondary'}
                size="xs"
                onClick={() => setTheme('auto')}
                className="flex-1"
              >
                🔄
              </Button>
            </div>
          </div>

          {/* Keyboard Shortcut Hints */}
          <div className="px-4 py-2 border-t border-border space-y-1">
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs">⌘P</kbd>
              <span>for project switcher</span>
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs">⌘K</kbd>
              <span>for command palette</span>
            </p>
          </div>

          {/* Logout */}
          {onLogout && (
            <div className="border-t border-border py-1">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDropdown(false);
                  onLogout();
                }}
                className="w-full justify-start px-4 py-2 text-sm text-destructive hover:bg-destructive-light transition-colors flex items-center gap-2 rounded-none h-auto"
              >
                <span>🚪</span>
                <span>Log out</span>
              </Button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================================================
// Tab Actions Menu - Three-dot menu for tab actions like Close All
// ============================================================================
function TabActionsMenu({
  onCloseAll,
  tabCount,
}: {
  onCloseAll: () => void;
  tabCount: number;
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isInsideButton = buttonRef.current?.contains(target);
      if (!isInsideDropdown && !isInsideButton) {
        setShowDropdown(false);
      }
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position for portal (anchor to right side)
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    
    setShowDropdown(!showDropdown);
  };

  const handleCloseAll = () => {
    onCloseAll();
    setShowDropdown(false);
  };

  return (
    <div className="px-2 py-2 flex-shrink-0">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        onClick={handleMenuClick}
        className="w-8 h-8 text-muted-foreground"
        title="Tab actions"
        aria-label="Tab actions"
        aria-expanded={showDropdown}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </Button>

      {/* Dropdown Menu (rendered via portal) */}
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-card rounded-lg shadow-xl border border-border py-1 w-48 z-[9999]"
          // eslint-disable-next-line no-restricted-syntax -- Dynamic positioning for portal
          style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
        >
          <Button
            variant="ghost"
            onClick={handleCloseAll}
            disabled={tabCount === 0}
            className="w-full justify-start px-4 py-2 text-sm text-foreground hover:bg-muted flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed rounded-none h-auto"
          >
            <span className="text-muted-foreground">✕</span>
            <span>Close All Tabs</span>
            {tabCount > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">
                ({tabCount})
              </span>
            )}
          </Button>
        </div>,
        document.body
      )}
    </div>
  );
}

interface OpenedDocument {
  id: string;
  tabId: number;
  title: string;
  tabType?: TabType; // 'document' | 'plan' | 'page' | 'diagram' | 'mermaid'
  // Document-specific fields
  documentNumber?: string;
  projectId?: string;
  type?: 'project' | 'company' | 'standard' | 'prior-project' | 'plan';
  canEdit?: boolean;
  // Plan-specific fields
  planDocument?: PlanDocument;
  // Page panel-specific fields
  panelType?: PagePanelId;
  // Diagram-specific fields (wiring diagrams)
  diagramId?: string;
  // Mermaid/Flow diagram-specific fields
  mermaidDiagramId?: string;
  // Coding project-specific fields
  codingProjectId?: string;
}

// Panel icons for page tabs
const panelIcons: Record<PagePanelId, string> = {
  'reference-library': '📚',
  'projects': '📁',
  'company-docs': '🏢',
  'doc-control': '📂',
  'quality': '📊',
  'validation': '✓',
  'crosswalk': '🔀',
  'suppliers': '🏭',
  'users': '👥',
  'my-profile': '👤',
  'preferences': '⚙️',
  'organization': '🏢',
  'billing': '💳',
};

// Status badge colors for plan tabs
const planStatusColors: Record<PlanStatus, { bg: string; text: string; activeBg: string }> = {
  draft: { bg: 'bg-muted', text: 'text-muted-foreground', activeBg: 'bg-muted' },
  ready: { bg: 'bg-info-light', text: 'text-info', activeBg: 'bg-info-light' },
  executing: { bg: 'bg-warning-light', text: 'text-warning', activeBg: 'bg-warning-light' },
  paused: { bg: 'bg-warning-light', text: 'text-warning', activeBg: 'bg-warning-light' },
  completed: { bg: 'bg-success-light', text: 'text-success', activeBg: 'bg-success-light' },
  failed: { bg: 'bg-destructive-light', text: 'text-destructive', activeBg: 'bg-destructive-light' },
  cancelled: { bg: 'bg-muted', text: 'text-muted-foreground', activeBg: 'bg-muted' },
};

interface DocumentViewerTabsProps {
  documents: OpenedDocument[];
  activeTabId: number | null;
  editingTabId: number | null;
  onSelectTab: (tabId: number) => void;
  onCloseTab: (tabId: number) => void;
  onCloseAll: () => void;
  onEnableEdit: (tabId: number) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  // Project workspace props
  activeProject?: ActiveProject | null;
  isProjectTabSelected?: boolean;
  onSelectProjectTab?: () => void;
  onSwitchProject?: (projectId: string | null) => void;
  // Document lock colors for visual pairing
  documentLockColors?: Record<string, LockColorCode>;
  // User avatar props
  userEmail?: string;
  onOpenSettings?: (panelId: PagePanelId) => void;
  onLogout?: () => void;
}

export default function DocumentViewerTabs({
  documents,
  activeTabId,
  editingTabId,
  onSelectTab,
  onCloseTab,
  onCloseAll,
  onEnableEdit: _onEnableEdit,
  onReorder,
  activeProject,
  isProjectTabSelected,
  onSelectProjectTab,
  onSwitchProject: _onSwitchProject,
  documentLockColors,
  userEmail,
  onOpenSettings,
  onLogout,
}: DocumentViewerTabsProps) {
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);
  
  // Artifact colors for visual pairing with conversation tabs
  const { getClasses, artifactColorMap } = useArtifactColors();
  
  // Auto-scroll active tab into view when it changes
  useEffect(() => {
    if (activeTabRef.current && tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const tab = activeTabRef.current;
      
      // Calculate if tab is outside visible area
      const containerRect = container.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      
      if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
        // Scroll tab into view with smooth behavior
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTabId]);

  // Note: Removed documents.length === 0 check - always render to show pinned Projects tab

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (_e: React.DragEvent) => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    
    if (fromIndex !== null && fromIndex !== toIndex && onReorder) {
      onReorder(fromIndex, toIndex);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  };

  return (
    <div className="flex items-center bg-background h-[45px] transition-colors">
      {/* Pinned User Avatar Tab (always visible, never scrolls) */}
      {userEmail && onOpenSettings && (
        <div className="flex-shrink-0 pl-2">
          <UserAvatarTab
            userEmail={userEmail}
            onOpenSettings={onOpenSettings}
            onLogout={onLogout}
          />
        </div>
      )}
      
      {/* Pinned Projects Tab (always visible, never scrolls) */}
      {onSelectProjectTab && (
        <div className="flex-shrink-0 pl-1">
          <ProjectsTab
            activeProject={activeProject || null}
            isSelected={isProjectTabSelected || false}
            onSelect={onSelectProjectTab}
          />
        </div>
      )}
      
      {/* Scrollable document tabs container */}
      <div 
        ref={tabsContainerRef}
        role="tablist"
        aria-label="Document tabs"
        className="flex items-center gap-1 px-2 py-2 overflow-x-auto flex-1 min-w-0 overscroll-contain" 
      >
        {documents.map((doc, index) => {
          const isActive = doc.tabId === activeTabId;
          const isEditing = doc.tabId === editingTabId;

          // Determine tab type
          // Plans now flow as regular documents, so also check jsonDocument metadata
          const isPlan = doc.tabType === 'plan' || doc.type === 'plan' || doc.jsonDocument?.metadata?.documentType === 'ai_plan';
          const isPage = doc.tabType === 'page' && doc.panelType;
          const isDiagram = doc.tabType === 'diagram';
          const isMermaid = doc.tabType === 'mermaid';
          const isCoding = doc.tabType === 'coding';
          const _isDocument = !isPlan && !isPage && !isDiagram && !isMermaid && !isCoding;
          
          // Plan status from planDocument or jsonDocument metadata
          const planStatus = doc.planDocument?.status || doc.jsonDocument?.metadata?.planStatus || 'draft';
          const statusColors = isPlan ? planStatusColors[planStatus] : null;

          const isDragOver = dragOverIndex === index && draggedIndex !== index;
          const isDragging = draggedIndex === index;
          
          // Check if document/diagram has an artifact color assigned (for linked tab visual pairing)
          // Only show color if artifact is linked to a conversation (has assigned color)
          // For wiring diagrams use diagramId, for mermaid/flowcharts use mermaidDiagramId, for coding use codingProjectId, for documents use documentNumber
          const artifactId = isDiagram ? doc.diagramId : isMermaid ? doc.mermaidDiagramId : isCoding ? doc.codingProjectId : doc.documentNumber;
          const hasArtifactColor = artifactId && artifactColorMap.has(artifactId);
          const assignedColorName = hasArtifactColor ? artifactColorMap.get(artifactId) : undefined;
          const artifactClasses = assignedColorName ? getClasses(assignedColorName) : undefined;
          
          // Also check legacy lock colors for backwards compatibility (documents only)
          const legacyLockColor = doc.documentNumber && documentLockColors 
            ? documentLockColors[doc.documentNumber] 
            : undefined;
          const legacyArtifactColorName = legacyLockColor ? LOCK_COLOR_TO_ARTIFACT[legacyLockColor] : undefined;
          const legacyClasses = legacyArtifactColorName ? getClasses(legacyArtifactColorName) : undefined;
          
          // Use artifact classes if assigned, otherwise legacy, otherwise none
          const effectiveClasses = artifactClasses || legacyClasses;

          // Get appropriate icon - checkmark for linked docs (matches conversation tabs)
          const getTabIcon = () => {
            if (isPage && doc.panelType) return panelIcons[doc.panelType] || '📄';
            if (isPlan) return '📋';
            // Show checkmark when document is linked to a conversation
            if (effectiveClasses) return 'checkmark';
            return null;
          };
          
          const tabIcon = getTabIcon();

          // Get appropriate background color
          // Priority: artifact colors (linked) > editing state > standard colors
          // Linked tabs: active shows bg, inactive just shows border accent
          const getTabBgClass = () => {
            // If document is linked to a conversation, use artifact colors
            if (effectiveClasses) {
              if (isActive) {
                return `${effectiveClasses.bg} ${effectiveClasses.text} shadow-sm`;
              }
              // Inactive linked: transparent bg, just border accent, reduced opacity
              return `bg-transparent ${effectiveClasses.bgHover} ${effectiveClasses.text} opacity-70 hover:opacity-100 transition-all`;
            }
            
            // Fallback: standard colors
            if (!isActive) return 'bg-card hover:bg-accent text-foreground transition-colors';
            if (isEditing) return 'bg-success text-success-foreground shadow-sm hover:bg-success/90';
            // All other active tabs (documents, plans, pages) use same info style
            return 'bg-info text-info-foreground shadow-sm hover:bg-info/90';
          };

          return (
            <div
              key={doc.tabId}
              className="relative flex-shrink-0"
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              {/* Drop indicator line - shows where tab will be inserted */}
              {isDragOver && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-info -ml-1 z-10" />
              )}
              <div
                ref={isActive ? activeTabRef : undefined}
                role="tab"
                aria-selected={isActive}
                aria-label={doc.documentNumber || doc.title || 'Document tab'}
                data-tab-id={doc.tabId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onClick={() => onSelectTab(doc.tabId)}
                className={`flex items-center gap-2 px-3 h-8 rounded flex-shrink-0 transition-all ${
                  isDragging ? 'opacity-50 cursor-grabbing' : 'cursor-pointer'
                } ${getTabBgClass()} ${effectiveClasses ? `border-l-[3px] ${effectiveClasses.borderLeft}` : ''}`}
              >
                {/* Tab Icon - checkmark SVG for linked docs, emoji for others */}
                {tabIcon === 'checkmark' ? (
                  <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                ) : tabIcon && (
                  <span className="text-xs">{tabIcon}</span>
                )}

                {/* Tab Title - use documentNumber for documents (matches conversation tabs) */}
                <span className={`text-xs font-medium truncate max-w-[150px] ${effectiveClasses ? '' : (isActive ? 'text-white' : 'text-foreground')}`}>
                  {isPlan 
                    ? doc.documentNumber || doc.title || 'Plan'
                    : isPage 
                    ? doc.title
                    : isDiagram || isMermaid
                    ? doc.title || 'Diagram'
                    : isCoding
                    ? doc.title || 'Coding Project'
                    : doc.documentNumber}
                </span>

                {/* Plan Status Badge */}
                {isPlan && statusColors && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    isActive ? statusColors.activeBg + ' text-foreground' : statusColors.bg + ' ' + statusColors.text
                  }`}>
                    {planStatus.toUpperCase()}
                  </span>
                )}

                {/* Close Button - uses artifact text color when linked for theme awareness */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCloseTab(doc.tabId);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label={isPlan ? "Close plan" : isPage ? "Close panel" : "Close document"}
                  className={`w-4 h-4 flex items-center justify-center rounded text-xs transition-colors ${
                    effectiveClasses
                      ? `${effectiveClasses.text} opacity-60 hover:opacity-100`
                      : isActive 
                        ? 'hover:bg-white/20 text-white/60 hover:text-white' 
                        : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                  title={isPlan ? "Close plan" : isPage ? "Close panel" : "Close document"}
                >
                  ✕
                </Button>

              </div>
            </div>
          );
        })}
        
        {/* End drop zone - allows dropping tab at end position */}
        {draggedIndex !== null && (
          <div
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverIndex(documents.length);
            }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedIndex !== null && onReorder) {
                onReorder(draggedIndex, documents.length);
                setDraggedIndex(null);
                setDragOverIndex(null);
              }
            }}
          >
            {dragOverIndex === documents.length && (
              <div className="w-0.5 h-6 bg-info rounded" />
            )}
          </div>
        )}
      </div>

      {/* Tab Actions Menu (three-dot menu) */}
      {documents.length > 0 && (
        <TabActionsMenu 
          onCloseAll={onCloseAll}
          tabCount={documents.length}
        />
      )}
    </div>
  );
}
